/**
 * How a shopper found this shop (feature 0035 slice A).
 *
 * It lives here, in the shared package, because THREE call sites need the same answer and they are in two
 * different apps: the storefront's measurement beacon, the classic checkout form, and the one-page COD buy
 * box (which ships from this package). Duplicating it would guarantee that the session and the order
 * eventually disagree about the same visit.
 *
 * Three constraints shaped every line below:
 *
 *  1. **It must never throw.** The checkout imports it. A shopper must never lose an order because our
 *     bookkeeping hit a quota, a disabled storage API, or a value someone hand-edited. Every read is guarded
 *     and every failure degrades to "we do not know" — which is a real answer here, not a silent zero.
 *  2. **It reports signals, never a channel.** The words SOCIAL / SEARCH / DIRECT are decided by
 *     `AttributionResolver` on the server. If this file picked a channel, that classification would exist in
 *     two places and would drift the first time either changed — and a wrong channel raises nothing, it just
 *     prints a plausible number.
 *  3. **Zero dependencies.** Browser APIs only. A bare import from this package once broke the storefront's
 *     client bundle and took the buy box and the cart down with it.
 *
 * **Storage: `sessionStorage`, tab lifetime, nothing persistent.** A 30-day record would carry a shopper's
 * journey across visits and let an order name the campaign that first brought them in — but it is terminal
 * storage whose purpose is to be joined to a NAMED order and shown to the merchant, which is not audience
 * measurement any more. The founder's call was to keep the window to the visit. The cost is stated rather
 * than hidden: a shopper who sees an Instagram post on Monday and returns on Wednesday reads as Direct on
 * Wednesday, unless the merchant labels their links — which is exactly what the campaign-link generator in
 * the dashboard is for.
 */
const KEY = 'aqx_attr';
const VERSION = 1;
/** The click identifiers we recognise. We keep the NAME and drop the value — see readTouch. */
const CLICK_IDS = ['fbclid', 'gclid', 'ttclid'];
const UTM_KEYS = [
    ['utm_source', 'source'],
    ['utm_medium', 'medium'],
    ['utm_campaign', 'campaign'],
    ['utm_content', 'content'],
    ['utm_term', 'term'],
];
/** Bound every value here too, so a hostile URL cannot make the beacon's own query string enormous. */
const MAX_VALUE = 128;
/** A host, not a URL — matching the server's own ceiling for the column. */
const MAX_REF = 255;
/**
 * Reads what the CURRENT page says about how the visitor got here.
 *
 * Returns `null` for an internal navigation carrying no labels — deliberately, and this is the subtle part.
 * A session re-opens after thirty idle minutes, and the page the visitor happens to be reading then becomes
 * that session's landing. Reporting "direct" for it would freeze a lie on a row that is never rewritten. So
 * an internal hop with nothing to say says nothing at all.
 */
export function readTouch(doc = document) {
    try {
        const url = new URL(doc.location.href);
        const touch = {};
        for (const [param, field] of UTM_KEYS) {
            const value = clip(url.searchParams.get(param), MAX_VALUE);
            if (value)
                touch[field] = value;
        }
        // The PRESENCE of a click id, never its value. `fbclid` and `gclid` are cross-site identifiers that Meta
        // and Google can tie back to a named account; keeping one for 400 days beside a city and a basket would
        // take this out of audience measurement entirely.
        const click = CLICK_IDS.find((name) => url.searchParams.has(name));
        if (click)
            touch.click = click;
        const ref = referrerOf(doc, url.host);
        if (ref)
            touch.ref = ref;
        touch.path = clip(url.pathname, MAX_VALUE) ?? '/';
        // A door of some kind: labels, a click id, or a referrer from elsewhere.
        if (touch.source || touch.medium || touch.click || touch.ref)
            return touch;
        // No door, and no referrer at all → the visitor arrived by typing, a bookmark, or an app that strips the
        // referrer (WhatsApp mobile does). That IS direct, and we say so on the record.
        if (!doc.referrer)
            return { ...touch, direct: true };
        // A referrer we dropped because it was our own host: an internal hop. Nothing to claim.
        return null;
    }
    catch {
        return null;
    }
}
/**
 * Records this page as a contact, and returns everything the checkout should send.
 *
 * `visit` is written once per tab and never overwritten: the landing is what the LANDING said, not what the
 * fourth page of the visit says. `last` moves forward on every new non-direct door, `first` never moves. On
 * an uninterrupted visit all three agree and the order shows one line.
 */
export function recordVisit(doc = document) {
    const touch = readTouch(doc);
    const stored = read();
    const next = { v: VERSION, ...stored };
    if (touch) {
        if (!next.visit)
            next.visit = touch;
        if (!touch.direct) {
            if (!next.first)
                next.first = touch;
            next.last = touch;
        }
    }
    write(next);
    return payload(next);
}
/**
 * What the checkout attaches to its request. Never throws, and returns `undefined` when there is nothing to
 * say — which the server reads as UNKNOWN, not as direct.
 */
export function attributionPayload() {
    const stored = read();
    if (!stored)
        return undefined;
    const result = payload(stored);
    return result.visit || result.first || result.last ? result : undefined;
}
/** Exported for the beacon, which sends the CURRENT page's contact as query parameters. */
export function touchParams(touch) {
    if (!touch)
        return {};
    const params = {};
    if (touch.source)
        params.us = touch.source;
    if (touch.medium)
        params.um = touch.medium;
    if (touch.campaign)
        params.uc = touch.campaign;
    if (touch.content)
        params.uo = touch.content;
    if (touch.term)
        params.ut = touch.term;
    if (touch.ref)
        params.ref = touch.ref;
    if (touch.click)
        params.ck = touch.click;
    if (touch.path)
        params.lp = touch.path;
    if (touch.direct)
        params.dir = '1';
    return params;
}
// ── internals ────────────────────────────────────────────────────────────────────────────────────
function payload(stored) {
    return { visit: stored.visit, first: stored.first, last: stored.last };
}
/**
 * The referrer's HOST, unless it is this same shop or the payment page we just came back from.
 *
 * Dropping our own host is the rule that stops a shop from being its own top traffic source — most page
 * loads on a storefront are referred by the storefront. Dropping the payment hosts is the same rule wearing
 * a different hat: a buyer returning from Stripe has not been referred by Stripe, and without this the card
 * funnel would overwrite a real Instagram touch with `checkout.stripe.com` on the last page before the
 * order. The server applies both rules again against the host it actually served, because this side is
 * client code and therefore advisory.
 */
function referrerOf(doc, ownHost) {
    const raw = doc.referrer;
    if (!raw)
        return undefined;
    try {
        // `android-app://com.instagram.android` is not parseable as a URL with a host — and it is a signal worth
        // keeping, so it is passed through whole for the server to recognise.
        if (raw.startsWith('android-app://'))
            return clip(raw, MAX_REF);
        const host = new URL(raw).host;
        if (!host || host === ownHost)
            return undefined;
        if (isPaymentHost(host))
            return undefined;
        return clip(host, MAX_REF);
    }
    catch {
        return undefined;
    }
}
/** Mirrors the server's list (`AttributionResolver.PAYMENT_HOSTS`). Kept in step by hand — it is six lines. */
function isPaymentHost(host) {
    const exact = ['checkout.stripe.com', 'js.stripe.com', 'hooks.stripe.com', 'pay.google.com'];
    if (exact.includes(host))
        return true;
    return host.endsWith('.stripe.com') || host.endsWith('.applepay.apple.com');
}
/**
 * Characters that must never reach the request body. The same set the server's `AttributionNormalizer`
 * strips, mirrored here because ONE of them does not merely look wrong — it makes the order fail.
 *
 * `%00` is a legal percent-encoding, so `?utm_source=insta%00gram` is a link anyone can post, and
 * `URLSearchParams` decodes it into a real NUL. Jackson refuses a NUL anywhere in a JSON body — "Text must
 * not contain NUL (0x00) characters" — which is a 400 on the WHOLE checkout, thrown before a line of our
 * code runs. Verified against the running backend on 2026-08-03: the payload a real browser built from that
 * URL came back `400 MALFORMED_REQUEST`. And because the payload is replayed from `sessionStorage`, every
 * retry in that tab fails identically. The bidi marks are the same rule for a milder reason: they render as
 * nothing while flipping the direction of everything after them in the merchant's dashboard.
 */
const HOSTILE = /[\u0000-\u001F\u007F-\u009F\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
function clip(value, max) {
    if (!value)
        return undefined;
    const trimmed = value.replace(HOSTILE, '').trim().slice(0, max);
    return trimmed.length > 0 ? trimmed : undefined;
}
const STRING_FIELDS = [
    'source',
    'medium',
    'campaign',
    'content',
    'term',
    'ref',
    'click',
    'path',
];
/**
 * Rebuilds one contact from whatever was in storage, keeping only what we would have written ourselves.
 *
 * The version gate is not enough on its own. Everything that reaches `sessionStorage` becomes the body of a
 * checkout request, and the server's DTO is a contract with a hard edge: a `visit` that is a string rather
 * than an object fails Jackson BEFORE any of our code runs, so the shopper gets a 400 on their order rather
 * than a missing source on the merchant's report. Verified against the running backend on 2026-08-03 —
 * `{"attribution":{"visit":"pwned"}}` → 400 MALFORMED_REQUEST, no order.
 *
 * So this rebuilds rather than validates: an unknown key is dropped, a non-string value is dropped, and each
 * survivor goes back through `clip`, which is what removes the NUL a `%00` in the landing URL would have put
 * there. A record that keeps nothing is treated as no record at all.
 */
function sanitise(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const source = value;
    const touch = {};
    for (const field of STRING_FIELDS) {
        const raw = source[field];
        if (typeof raw !== 'string')
            continue;
        const clipped = clip(raw, field === 'ref' ? MAX_REF : MAX_VALUE);
        if (clipped)
            touch[field] = clipped;
    }
    // Only `true` means anything: `direct: false` is the absence of a claim, and it is what the server would
    // have inferred anyway.
    if (source.direct === true)
        touch.direct = true;
    return Object.keys(touch).length > 0 ? touch : undefined;
}
function read() {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw)
            return undefined;
        const parsed = JSON.parse(raw);
        // A hand-edited or older record is discarded rather than trusted. Losing one visit's provenance is a
        // rounding error; letting a malformed shape reach the checkout body costs the order itself.
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
            return undefined;
        const record = parsed;
        if (record.v !== VERSION)
            return undefined;
        const clean = { v: VERSION };
        const visit = sanitise(record.visit);
        const first = sanitise(record.first);
        const last = sanitise(record.last);
        if (visit)
            clean.visit = visit;
        if (first)
            clean.first = first;
        if (last)
            clean.last = last;
        return clean;
    }
    catch {
        return undefined;
    }
}
function write(value) {
    try {
        sessionStorage.setItem(KEY, JSON.stringify(value));
    }
    catch {
        // Safari private mode, a storage quota, a hardened browser. The visit is still reported to the beacon
        // from the page itself — only the carry-over to the checkout is lost, and that reads as UNKNOWN.
    }
}
//# sourceMappingURL=attribution.js.map